import { useMutation, useQuery, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { PaywallCoupon } from "@/react/types/PaywallCoupon";
import { paywallCouponApi } from "@circle-react/api/paywallCouponApi";
import { useToast } from "@circle-react-uikit/ToastV2";

interface UsePaywallCouponApiProps {
  onClose?: (() => void) | null;
  paywallCoupon?: PaywallCoupon | null;
}

interface PaywallCouponFormData {
  [key: string]: unknown;
}

export const usePaywallCouponApi = ({
  onClose = null,
  paywallCoupon = null,
}: UsePaywallCouponApiProps) => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();
  const REQUEST_KEY = "paywall-coupons";

  const useListPaywallCoupons = () => {
    const queryFn = () =>
      reactQueryGet<PaywallCoupon[]>(internalApi.paywallCoupons.index());

    return useQuery<PaywallCoupon[]>(REQUEST_KEY, queryFn);
  };

  const useShowPaywallCoupon = (paywallCouponId: number) => {
    const queryKey = ["paywall-coupon", paywallCouponId];
    const queryFn = () => paywallCouponApi.showPaywallCoupon(paywallCouponId);

    return useQuery<PaywallCoupon>(queryKey, queryFn, {
      enabled: Boolean(paywallCouponId),
    });
  };

  const updateMutation = useMutation(
    (formData: PaywallCouponFormData) =>
      paywallCouponApi.updatePaywallCoupon(paywallCoupon?.id ?? 0, formData),
    {
      onSuccess: () => {
        success(t("settings.paywall_coupons.alerts.success.updated"));
        onClose?.();
        void queryClient.invalidateQueries(REQUEST_KEY);
      },
      onError: (updateError: { errorDetails: string }) => {
        console.error(updateError.errorDetails);
        error(updateError.errorDetails);
      },
    },
  );

  const createMutation = useMutation(
    (formData: PaywallCouponFormData) =>
      paywallCouponApi.createPaywallCoupon(formData),
    {
      onSuccess: () => {
        success(t("settings.paywall_coupons.alerts.success.created"));
        onClose?.();
        void queryClient.invalidateQueries(REQUEST_KEY);
      },
      onError: (createError: { errorDetails: string }) => {
        console.error(createError.errorDetails);
        error(createError.errorDetails);
      },
    },
  );

  const destroyMutation = useMutation(
    () => paywallCouponApi.destroyPaywallCoupon(paywallCoupon?.id ?? 0),
    {
      onSuccess: () => {
        success(t("settings.paywall_coupons.alerts.success.deleted"));
        void queryClient.invalidateQueries(REQUEST_KEY);
      },
      onError: (destroyError: { message: string }) => {
        console.error(destroyError.message);
        error(destroyError.message, {
          duration: "long",
        });
      },
    },
  );

  return {
    useListPaywallCoupons,
    useShowPaywallCoupon,
    updateMutation,
    createMutation,
    destroyMutation,
  };
};
